import { isSSR } from '~/utils/dom';
import type { MenuPageEvent, MenuPageState } from '../../types';

const initialViewState: MenuPageState = {
  filters: [],
  focusedSectionId: -1,
  intersectingSections: [],
  sectionIntersectionsWithRatios: [],
  sectionsWithFilteredItems: [],
};

// Handle view event types here. Cases are determined by existing state value, then type of event in that existing state
export const menuPageReducer = (prevState: MenuPageState = initialViewState, event: MenuPageEvent): MenuPageState => {
  if (isSSR) {
    return prevState;
  }

  if (event.type === 'ordering.resetMenuPage') {
    return initialViewState;
  }

  const nextState = { ...prevState };
  switch (event.type) {
    case 'updateFilters': {
      const match = nextState.filters.find(filter => filter.id === event.filter.id);
      if (match) {
        nextState.filters = nextState.filters.filter(filter => filter.id !== match.id);
      } else {
        nextState.filters = [...nextState.filters, event.filter];
      }
      nextState.sectionsWithFilteredItems = [];
      break;
    }
    case 'updateSectionFilteredItems': {
      if (event.hasItems) {
        // Add the new section ID and ensure uniqueness
        nextState.sectionsWithFilteredItems = [
          ...nextState.sectionsWithFilteredItems,
          event.sectionId,
        ].filter((element, index, array) => array.indexOf(element) === index);
      } else {
        // Remove the section ID
        nextState.sectionsWithFilteredItems = nextState.sectionsWithFilteredItems
          .filter(id => id !== event.sectionId);
      }
      break;
    }
    case 'syncObservedSections': {
      const prunedIntersecting = nextState.intersectingSections.filter(id => event.sectionIds.includes(id));
      const prunedRatios = nextState.sectionIntersectionsWithRatios.filter(({ sectionId }) => event.sectionIds.includes(sectionId));
      if (prunedIntersecting.length !== nextState.intersectingSections.length || prunedRatios.length !== nextState.sectionIntersectionsWithRatios.length) {
        nextState.intersectingSections = prunedIntersecting;
        nextState.sectionIntersectionsWithRatios = prunedRatios;
      }
      if (nextState.focusedSectionId !== -1 && !event.sectionIds.includes(nextState.focusedSectionId)) {
        nextState.focusedSectionId = -1;
      }
      break;
    }
    case 'updatePreloadIntersections': {
      const entering = event.sectionIntersections.filter(({ isIntersecting }) => isIntersecting).map(({ sectionId }) => sectionId);
      const leaving = event.sectionIntersections.filter(({ isIntersecting }) => !isIntersecting).map(({ sectionId }) => sectionId);
      nextState.intersectingSections = [
        ...nextState.intersectingSections.filter(id => !leaving.includes(id)),
        ...entering.filter(id => !nextState.intersectingSections.includes(id)),
      ];
      break;
    }
    case 'updateSectionIntersections':
      nextState.sectionIntersectionsWithRatios = [...nextState.sectionIntersectionsWithRatios, ...event.sectionIntersections].reduce<MenuPageState['sectionIntersectionsWithRatios']>((accumulator, currentSection) => {
        const matchIndex = accumulator.findIndex(({ sectionId }) => sectionId === currentSection.sectionId);
        if (matchIndex === -1) {
          accumulator.push(currentSection);
        } else {
          accumulator.splice(matchIndex, 1);
          accumulator.push(currentSection);
        }
        return accumulator;
      }, []);
      {
        const validSections = nextState.sectionIntersectionsWithRatios.filter(({ sectionId }) => sectionId !== -1);
        const mostVisible = validSections.reduce(
          (acc, section) => (section.visibleHeight >= acc.visibleHeight ? section : acc),
          { ratio: 0, sectionId: -1, visibleHeight: 0 },
        );
        nextState.focusedSectionId = mostVisible.sectionId;
      }
      break;
    case 'sectionSelected': {
      const isSectionFullyVisible = nextState.sectionIntersectionsWithRatios.find(({ sectionId, ratio }) => sectionId === event.selectedSectionId && ratio === 1);
      if (isSectionFullyVisible) {
        nextState.focusedSectionId = event.selectedSectionId;
      }
      break;
    }
    case 'clearFilters':
      nextState.filters = [];
      nextState.sectionsWithFilteredItems = [];
      break;
    default:
      break;
  }
  return nextState;
};
